<template>
  <BackgroundImageComponent />

  <ConfirmModalComponent
    ref="confirmDeleteDialog"
    title="Trainingseinheit löschen?"
    message="Soll die Trainingseinheit wirklich gelöscht werden?"
    @confirm="confirmDelete"
  ></ConfirmModalComponent>

  <div class="position-relative dashboard">
    <div class="card mb-4">
      <div class="card-body">
        <div
          class="d-flex flex-column flex-md-row justify-content-between align-items-center"
        >
          <h1 class="text-ruderclub-orange">Dashboard</h1>
          <button
            type="button"
            @click="oeffneTrainingAnlegenModalComponent"
            class="d-flex btn btn-ruderclub-dark-green"
          >
            <i class="material-symbols-outlined me-2">add</i>Training anlegen
          </button>
        </div>
      </div>
    </div>

    <!-- Benutzer-Auswahl für Admins -->
    <div v-if="istAdmin" class="mb-4 input-group">
      <span class="input-group-text">
        <span class="material-symbols-outlined"> person </span>
      </span>
      <select
        id="benutzerSelect"
        v-model="selectedBenutzer"
        class="form-select"
        @change="loadData"
      >
        <option
          v-for="benutzer in benutzerListe"
          :key="benutzer.id"
          :value="benutzer.id"
        >
          {{ benutzer.name }}
        </option>
      </select>
    </div>

    <div v-if="!trainingsmethodenListe.length">
      <div class="row">
        <div class="col-12">
          <div class="alert alert-info">
            <strong>
              <span class="me-2">🎉</span>
              Schön, dass Du da bist. Lege Dein erstes Training an, um hier
              Deine Trainingsdaten zu sehen.
            </strong>
          </div>
        </div>
      </div>
    </div>

    <!-- Karten für jede Trainingsmethode -->
    <div v-if="trainingsmethodenListe.length > 0">
      <div class="row">
        <div
          class="col-12 col-md-6 col-lg-6"
          v-for="trainingsmethode in trainingsmethodenListe"
          :key="trainingsmethode.id"
        >
          <div class="card border-ruderclub-orange shadow mb-4">
            <div
              class="card-header d-flex justify-content-between align-items-center bg-ruderclub-dark-green"
            >
              <TrainingsmethodeIconComponent
                style="height: 40px; width: 40px"
                class="d-flex justify-content-center badge rounded-pill text-dark border-dark"
                :class="{
                  'bg-info': trainingsmethode.id === 'rudern',
                  'bg-success': trainingsmethode.id === 'fahrradfahren',
                  'bg-warning': trainingsmethode.id === 'laufen',
                }"
                :trainingsmethode-id="trainingsmethode.id"
              ></TrainingsmethodeIconComponent>
              <h5 class="card-title mb-0">{{ trainingsmethode.name }}</h5>
              <button
                class="btn btn-link bg-ruderclub-dark-green"
                @click="toggleCard(trainingsmethode.id)"
                aria-expanded="false"
                aria-controls="collapseExample"
              >
                <span class="material-symbols-outlined">
                  {{
                    trainingsmethode.isOpen
                      ? "keyboard_arrow_up"
                      : "keyboard_arrow_down"
                  }}
                </span>
              </button>
            </div>
            <div class="card-body" v-show="trainingsmethode.isOpen">
              <div v-if="diagrammdatenVorhanden">
                <!-- Diagramm -->
                <canvas
                  :id="'chart-' + selectedBenutzer + '-' + trainingsmethode.id"
                  class="mb-4 chart-canvas"
                ></canvas>
              </div>

              <!-- Tabelle -->
              <div
                v-if="
                  trainingsmethode.daten && trainingsmethode.daten.length > 0
                "
                class="table-responsive"
              >
                <table class="table table-sm">
                  <thead>
                    <tr>
                      <th>Datum</th>
                      <th class="title-column">Titel</th>
                      <th>Distanz (km)</th>
                      <th>Dauer (HH:MM:SS)</th>
                      <th></th>
                      <!-- Spalte für Löschen-Button -->
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(einheit, index) in trainingsmethode.daten"
                      :key="index"
                      :class="einheit.tableRowBgColor"
                      @mouseover="showTooltip(trainingsmethode.id, einheit)"
                      @mouseout="hideTooltip(trainingsmethode.id)"
                      @touchstart="showTooltip(trainingsmethode.id, einheit)"
                      @touchend="hideTooltip(trainingsmethode.id)"
                      style="vertical-align: middle"
                    >
                      <td>
                        {{
                          einheit.datumUhrzeit
                            ? formatDate(datumOhneUhrzeit(einheit.datumUhrzeit))
                            : "Unbekannt"
                        }}
                      </td>
                      <td class="title-column">{{ einheit.titel }}</td>
                      <td>{{ (einheit.distanz / 1000).toFixed(3) }}</td>
                      <td>{{ einheit.dauer }}</td>
                      <td>
                        <button
                          v-if="
                            einheit.benutzer === firebaseAuth().currentUser.uid
                          "
                          type="button"
                          class="btn btn-sm btn-outline-danger"
                          style="display: inline-flex; position: relative"
                          @click="showConfirmDeleteDialog(einheit.id)"
                        >
                          <span class="material-symbols-outlined">
                            delete
                          </span>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="text-center">Noch keine Daten</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { onAuthStateChanged } from "firebase/auth";
import { firebaseAuth, firebaseDb } from "@/main";
import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  deleteDoc,
} from "firebase/firestore";
import Chart from "chart.js/auto";
import TrainingsmethodeIconComponent from "@/components/Shared/TrainingsmethodeIconComponent.vue";
import { notify } from "@kyvg/vue3-notification";
import BackgroundImageComponent from "@/components/Shared/BackgroundImageComponent.vue";
import ConfirmModalComponent from "@/components/Shared/ConfirmModalComponent.vue";

export default {
  components: {
    ConfirmModalComponent,
    BackgroundImageComponent,
    TrainingsmethodeIconComponent,
  },
  data() {
    return {
      trainingsmethodenListe: [],
      benutzerListe: [],
      istAdmin: false,
      selectedBenutzer: null,
      chartsMap: new Map(),
    };
  },
  mounted() {
    onAuthStateChanged(firebaseAuth, async (user) => {
      if (!user) {
        this.$router.push("/login");
      } else {
        this.selectedBenutzer = firebaseAuth.currentUser.uid;
        await this.loadData();
      }
    });
  },
  computed: {
    diagrammdatenVorhanden() {
      const vorhanden =
        this.benutzerListe.length && this.trainingsmethodenListe.length;

      if (vorhanden) {
        this.trainingsmethodenListe.forEach((trainingsmethode) => {
          this.createChart(trainingsmethode);
        });
      }

      return vorhanden;
    },
  },
  methods: {
    firebaseAuth() {
      return firebaseAuth;
    },
    datumOhneUhrzeit(datumUhrzeit) {
      return datumUhrzeit ? datumUhrzeit.split("T")[0] : null;
    },
    toggleCard(methodeId) {
      const methode = this.trainingsmethodenListe.find(
        (m) => m.id === methodeId
      );

      if (methode) {
        methode.isOpen = !methode.isOpen;
      }
    },

    oeffneTrainingAnlegenModalComponent() {
      this.$root.$refs.trainingseinheitAnlegenModal.open();
    },

    showTooltip(chartId, einheit) {
      const chart = this.chartsMap.get(chartId);

      if (!chart) return;

      // Finde den Index des Labels, das zum Datum passt
      const datasetContentIndex = chart.data.labels.findIndex((label) => {
        return (
          label === this.formatDate(this.datumOhneUhrzeit(einheit.datumUhrzeit))
        );
      });

      if (datasetContentIndex === -1) return;

      // Setze den Tooltip-Index
      const chartArea = chart.chartArea;
      chart.setActiveElements(
        [
          { datasetIndex: 0, index: datasetContentIndex }, // Distanz (km)
          { datasetIndex: 1, index: datasetContentIndex }, // Dauer (Stunden)
        ],
        {
          x: (chartArea.left + chartArea.right) / 2,
          y: (chartArea.top + chartArea.bottom) / 2,
        }
      );

      chart.tooltip.update(); // Tooltip aktualisieren
      /**
       * ToDo: Fehler bei chart.update() beheben
       * - chart.tooltip.update() tut nichts
       * - chart.update() ist die dokumentierte Methode
       * - führt aktuell zu RangeError: Maximum call stack size exceeded
       */
    },
    hideTooltip(chartId) {
      const chart = this.chartsMap.get(chartId);
      if (!chart) return;

      // Tooltip deaktivieren
      chart.tooltip.setActiveElements([], { x: 0, y: 0 });
      chart.tooltip.update();
    },
    async loadData() {
      try {
        const userDocRef = doc(
          firebaseDb,
          "benutzer",
          firebaseAuth.currentUser.uid
        );
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          this.istAdmin = userDocSnap.data().istAdmin;
        }

        if (this.istAdmin) {
          const benutzerSnap = await getDocs(
            collection(firebaseDb, "benutzer")
          );
          this.benutzerListe = benutzerSnap.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
        }

        await this.loadTrainingsmethoden();
      } catch (error) {
        console.error("Fehler beim Laden der Daten:", error);
      }
    },

    // Einfache Hash-Funktion, um das Datum zu einem Wert zu konvertieren
    getDateHash(date) {
      return Array.from(date).reduce(
        (acc, char) => acc + char.charCodeAt(0),
        0
      );
    },

    async loadTrainingsmethoden() {
      try {
        const methodenSnap = await getDocs(
          collection(firebaseDb, "trainingsmethoden")
        );
        const allMethoden = methodenSnap.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().name,
          daten: [],
          aggregierteDaten: [],
          isOpen: true, // Standardwert
        }));

        const trainingseinheiten = await this.loadAllTrainingseinheiten();

        this.trainingsmethodenListe = allMethoden
          .map((methode) => {
            methode.daten = this.filterTrainingseinheitenByMethode(
              methode.id,
              trainingseinheiten
            );
            methode.aggregierteDaten = this.aggregateDataByDate(methode.daten);

            // Füge die `tableRowBgColor` für jede `einheit` hinzu
            methode.daten.forEach((einheit) => {
              const date = this.datumOhneUhrzeit(einheit.datumUhrzeit);
              // Berechne den Hash des Datums (z. B. mit modulo)
              const dateHash = this.getDateHash(date);
              // Alterniere zwischen 'table-light' (hell) und 'table-dark' (dunkel)
              einheit.tableRowBgColor =
                dateHash % 2 === 0 ? "table-light" : "table-secondary";
            });

            return methode;
          })
          .filter((methode) => methode.daten.length > 0); // Nur Methoden mit Daten
      } catch (error) {
        console.error("Fehler beim Laden der Trainingsmethoden:", error);
      }
    },

    async loadAllTrainingseinheiten() {
      if (!this.selectedBenutzer) return [];
      const trainingseinheitenRef = collection(
        firebaseDb,
        "trainingseinheiten"
      );
      const querySnapshot = await getDocs(
        query(
          trainingseinheitenRef,
          where("benutzer", "==", this.selectedBenutzer) // Nur Daten des ausgewählten Benutzers
        )
      );
      return querySnapshot.docs.map((doc) => ({
        id: doc.id, // Dokument-ID speichern
        ...doc.data(),
      }));
    },

    showConfirmDeleteDialog(trainingseinheitId) {
      this.trainingseinheitIdToDeleteId = trainingseinheitId;
      this.$refs.confirmDeleteDialog.show();
    },

    async confirmDelete() {
      if (this.trainingseinheitIdToDeleteId) {
        try {
          // Referenz zur Trainingseinheit in Firebase
          const docRef = doc(
            firebaseDb,
            "trainingseinheiten",
            this.trainingseinheitIdToDeleteId
          );

          // Löschen aus Firebase
          await deleteDoc(docRef);

          // Lokale Daten aktualisieren
          this.trainingsmethodenListe.forEach((methode) => {
            methode.daten = methode.daten.filter(
              (einheit) => einheit.id !== this.trainingseinheitIdToDeleteId
            );
          });

          notify({
            title: "Erfolg",
            text: "Trainingseinheit erfolgreich gelöscht!",
            type: "success",
          });
        } catch (error) {
          console.error("Fehler beim Löschen der Trainingseinheit:", error);
          alert("Fehler: Die Trainingseinheit konnte nicht gelöscht werden.");
        }
      }
    },

    filterTrainingseinheitenByMethode(methodeId, trainingseinheiten) {
      const fehlerhafteTrainingseinheiten = trainingseinheiten.filter(
        (einheit) => !einheit.datumUhrzeit
      );

      if (fehlerhafteTrainingseinheiten.length) {
        console.warn(
          "Fehlerhafte Trainingseinheit gefunden:",
          fehlerhafteTrainingseinheiten
        );
      }

      return trainingseinheiten
        .filter(
          (einheit) =>
            einheit.trainingsmethode === methodeId && einheit.datumUhrzeit
        )
        .sort((a, b) => new Date(b.datumUhrzeit) - new Date(a.datumUhrzeit));
    },

    aggregateDataByDate(data) {
      const aggregated = data.reduce((acc, curr) => {
        const date = this.datumOhneUhrzeit(curr.datumUhrzeit);
        if (!date) return acc; // Überspringe ungültige Einträge

        if (!acc[date]) {
          acc[date] = { datum: date, distanz: 0, dauer: 0 };
        }
        acc[date].distanz += curr.distanz / 1000; // Distanz in km
        acc[date].dauer += this.convertDurationToSeconds(curr.dauer);
        return acc;
      }, {});
      return Object.values(aggregated);
    },

    createChart(methode) {
      this.$nextTick(() => {
        const ctxElement = document.getElementById(
          `chart-${this.selectedBenutzer}-${methode.id}`
        );
        if (!ctxElement) return;

        const ctx = ctxElement.getContext("2d");
        if (!ctx) return;

        // Sortiere die Daten für das Diagramm (älteste zuerst)
        const sortedData = [...methode.aggregierteDaten].sort(
          (a, b) => new Date(a.datum) - new Date(b.datum)
        );

        // Labels und Daten für das Diagramm basierend auf der sortierten Liste
        const labels = sortedData.map((e) => this.formatDate(e.datum));
        const distanzData = sortedData.map((e) => e.distanz);
        const dauerData = sortedData.map(
          (e) => (e.dauer / 3600).toFixed(2) // Dauer in Stunden
        );

        // Diagramm erstellen
        const chart = new Chart(ctx, {
          type: "line",
          data: {
            labels,
            datasets: [
              {
                label: "Distanz (km)",
                data: distanzData,
                borderColor: "#032B2A",
                backgroundColor: "rgba(3,43,42,0.8)",
              },
              {
                label: "Dauer (Stunden)",
                data: dauerData,
                borderColor: "#F99F1B",
                backgroundColor: "rgba(249,159,27,0.8)",
              },
            ],
          },
          options: {
            responsive: true,
            interaction: {
              intersect: false,
              mode: "index",
            },
            plugins: {
              tooltip: {
                enabled: true,
                intersect: false,
              },
            },
            scales: {
              x: {
                title: {
                  display: true,
                  text: "Datum",
                },
              },
              y: {
                title: {
                  display: true,
                  text: "Distanz (km)",
                },
              },
            },
          },
        });

        // Diagramm in der Map speichern
        this.chartsMap.set(methode.id, chart);
      });
    },

    convertDurationToSeconds(dauer) {
      if (!dauer || !dauer.includes(":")) return 0;
      const [hours, minutes, seconds] = dauer.split(":").map(Number);
      return hours * 3600 + minutes * 60 + seconds;
    },
    formatDate(date) {
      const [year, month, day] = date.split("-");
      return `${day}.${month}.${year}`;
    },
  },
};
</script>

<style scoped>
.dashboard {
  max-width: 1200px;
  text-align: center;
}

.card {
  margin-bottom: 1.5rem;
}

.card-header {
  cursor: pointer;
}

.card-body {
  padding: 20px;
}

.title-column {
  word-wrap: break-word; /* Falls ein altes Browser-Fallback nötig ist */
  overflow-wrap: break-word; /* Für modernes CSS */
  white-space: normal; /* Damit der Text umbrochen wird */
  vertical-align: middle; /* Vertikale Zentrierung */
  text-align: left;
  max-width: 400px;

  @media (max-width: 480px) {
    max-width: 200px; /* Maximalbreite der Spalte */
  }
}

.table-responsive {
  overflow-x: auto;
  overflow-y: hidden;
  display: block;
  white-space: initial; /* Falls nötig */
}

.table-responsive table {
  width: 100%;
  min-width: 600px; /* Erzwingt horizontales Scrollen */
}

table th,
table td {
  align-content: center;
  text-align: center;
}

.chart-canvas {
  max-width: 100%;
  height: auto;
}
</style>
